"use client";

import { useEffect, useRef } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import Link from 'next/link';
import './cash-working-capital.css';

/* All styling lives in cash-working-capital.css (no styling in this file).
   Images (public/assets/images/ me rakho):
   - cash-working-capital-hero (1).jpg -> hero background       (CSS: ".cw-hero-bg-image")
   - Better-Liquidity (1).jpg          -> benefit card 01        (CSS: ".cw-benefit-card--1")
   - Data-Driven-Decisions.jpg         -> benefit card 04        (CSS: ".cw-benefit-card--4")
   - AI-Enabled-Use-Cases 1.jpg        -> AI use-case visual     (below, in the use-cases section)
   - /assets/images/*.jpg              -> industry cards (see `industries` array below)
   - cash-capital-cta.jpg              -> final CTA background
   File names me space aur brackets hain, isliye URL me %20 use hua hai. */

/* ---------- Scroll reveal (bottom to top) ---------- */

function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const items = root.querySelectorAll<HTMLElement>('[data-reveal]');

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
    );

    items.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return ref;
}

const delay = (i: number, step = 80, max = 400): CSSProperties =>
  ({ '--cw-delay': `${Math.min(i * step, max)}ms` } as CSSProperties);

type IconKey =
  | 'box'
  | 'trend'
  | 'pin'
  | 'refresh'
  | 'cart'
  | 'shield'
  | 'bulb'
  | 'search'
  | 'network'
  | 'alert'
  | 'chat'
  | 'file'
  | 'chart'
  | 'download';

function Icon({ name }: { name: IconKey }) {
  const paths: Record<IconKey, ReactNode> = {
    box: <><path d="m12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Z" /><path d="m4 7.5 8 4.5 8-4.5M12 12v9" /></>,
    trend: <><path d="M3 19h18M5 16l5-5 3 3 6-8" /><path d="M14 6h5v5" /></>,
    pin: <><path d="M20 10c0 5-8 12-8 12S4 15 4 10a8 8 0 1 1 16 0Z" /><circle cx="12" cy="10" r="2.5" /></>,
    refresh: <><path d="M20 7v5h-5M4 17v-5h5" /><path d="M5.5 9A7 7 0 0 1 18 6l2 6M4 12l2 6a7 7 0 0 0 12.5-3" /></>,
    cart: <><path d="M3 4h2l2.2 11.2a2 2 0 0 0 2 1.6h8.9a2 2 0 0 0 1.9-1.4L22 8H6" /><circle cx="10" cy="20" r="1" /><circle cx="18" cy="20" r="1" /></>,
    shield: <><path d="M12 3 5 6v5c0 4.5 3 8 7 10 4-2 7-5.5 7-10V6z" /><path d="m9 12 2 2 4-4" /></>,
    bulb: <><path d="M9 18h6M10 22h4M8.5 14.5A7 7 0 1 1 15.5 14.5c-.8.7-1.2 1.5-1.3 2.5h-4.4c-.1-1-.5-1.8-1.3-2.5Z" /></>,
    search: <><circle cx="10.5" cy="10.5" r="6.5" /><path d="m16 16 5 5M8 10.5h5M10.5 8v5" /></>,
    network: <><circle cx="12" cy="5" r="2.5" /><circle cx="5" cy="19" r="2.5" /><circle cx="19" cy="19" r="2.5" /><path d="M12 7.5v4M12 11.5l-6 5.5M12 11.5l6 5.5" /></>,
    alert: <><path d="M12 3 2.8 20h18.4L12 3Z" /><path d="M12 9v5M12 17h.01" /></>,
    chat: <><path d="M4 5.5A3.5 3.5 0 0 1 7.5 2h9A3.5 3.5 0 0 1 20 5.5v8a3.5 3.5 0 0 1-3.5 3.5H10l-6 4v-6.5Z" /><path d="M8 9h8M8 13h5" /></>,
    file: <><path d="M6 3h8l5 5v13H6z" /><path d="M14 3v5h5M9 13h7M9 17h7" /></>,
    chart: <><path d="M4 20V4M4 20h17M8 16v-4M12 16V8M16 16v-6" /></>,
    download: <><path d="M12 3v12m-5-5 5 5 5-5M4 20h16" /></>,
  };

  return (
    <svg
      className="cw-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}

/* ============================== DATA ============================== */

const benefits: { title: string; text: string }[] = [
  { title: 'Better Liquidity', text: 'Convert more assets to cash' },
  { title: 'Lower Cost of Capital', text: 'Reduce borrowing needs' },
  { title: 'Improved Forecasting', text: 'Predict cash needs with accuracy' },
  { title: 'Data-Driven Decisions', text: 'Real-time insights across the value chain' },
  { title: 'Enable Profitable Growth', text: 'Invest in the right opportunities' },
];

const questions: { icon: IconKey; tone: string; text: string }[] = [
  { icon: 'trend', tone: 'amber', text: 'How much cash will we have next quarter?' },
  { icon: 'search', tone: 'green', text: 'Where are we losing working capital?' },
  { icon: 'box', tone: 'purple', text: 'How can we reduce inventory days?' },
  { icon: 'alert', tone: 'rose', text: 'Which customers are likely to delay payment?' },
  { icon: 'refresh', tone: 'blue', text: 'How can we optimise payables without hurting supplier relationships?' },
];

/* href = industry page. Slugs must match the routes used in the header Industries dropdown. */
const industries = [
  { title: 'Retail & CPG', desc: 'Reduce DSO, optimise inventory and improve trade working capital.', image: '/assets/images/Retail-&-CPG.jpg', href: '/industries/fmcg-retail' },
  { title: 'Manufacturing', desc: 'Optimise raw material and finished goods inventory.', image: '/assets/images/manufacturing1.jpg', href: '/industries/manufacturing' },
  { title: 'Pharma & Healthcare', desc: 'Manage high-value inventories and ensure timely receivables.', image: '/assets/images/Pharma-&-Healthcare.jpg', href: '/industries/pharma-lifescience' },
  { title: 'Auto & Industrial', desc: 'Synchronise supply chain and dealer working capital.', image: '/assets/images/Auto-&-Industrial1.jpg', href: '/industries/manufacturing' },
  { title: 'Utilities & Energy', desc: 'Manage receivables, fuel inventory and regulatory cash cycles.', image: '/assets/images/Utilities-&-Energy.jpg', href: '/industries/energy-utilities' },
  { title: 'Professional Services', desc: 'Improve project cash flow and manage DSO effectively.', image: '/assets/images/professional-services.jpg', href: '/industries/professional-services' },
];

const useCases: { title: string; desc: string }[] = [
  { title: 'Cash Flow Forecasting', desc: 'Predict short and medium-term cash positions.' },
  { title: 'Receivables Risk Scoring', desc: 'Identify at-risk customers and suggest actions.' },
  { title: 'Inventory Optimisation', desc: 'AI-based demand forecasting and replenishment recommendations.' },
  { title: 'Payables Optimisation', desc: 'Recommend optimal payment timing based on discounts, cash and supplier terms.' },
  { title: 'Working Capital Scenario Modelling', desc: 'Simulate business scenarios and assess impact on cash.' },
  { title: 'Conversational Working Capital Analyst', desc: 'Ask questions in natural language about cash, AR, AP and inventory.' },
];

const problems = [
  'High DSO and delayed receivables',
  'Excess and obsolete inventory',
  'Long supplier lead times',
  'Poor cash flow forecasting',
  'Working capital trapped in WIP',
];

const resources: { title: string; type: string; tone: string }[] = [
  { title: 'Cash Flow Assessment Template', type: 'Excel', tone: 'green' },
  { title: 'DSO Reduction Playbook', type: 'PDF', tone: 'rose' },
  { title: 'Working Capital Dashboard', type: 'Power BI', tone: 'amber' },
  { title: 'Inventory Optimisation Case Study', type: 'PDF', tone: 'rose' },
  { title: 'Working Capital Maturity Model', type: 'PDF', tone: 'rose' },
];

const journey = [
  { title: 'Digital Day', desc: 'Align on business questions and opportunities' },
  { title: 'Discovery & Assessment', desc: 'Analyse current state, data and quick wins' },
  { title: 'Solution Demo', desc: 'Showcase tailored use cases with your data' },
  { title: 'Pilot', desc: 'Prove value in a focused area (4–8 weeks)' },
  { title: 'Scale', desc: 'Enterprise rollout and continuous improvement' },
];

/* ============================== PAGE ============================== */

export default function CashWorkingCapitalIntelligence() {
  const ref = useScrollReveal();

  return (
    <div className="cw-page" ref={ref}>
      <main>
        {/* ============================= HERO ============================= */}
        <section className="cw-hero" data-purpose="hero-section" id="top">
          <div className="cw-hero-bg-image"></div>
          <div className="cw-hero-bg-overlay"></div>
          <div className="cw-hero-grid"></div>
          <div className="cw-hero-glow cw-hero-glow--left"></div>
          <div className="cw-hero-glow cw-hero-glow--right"></div>

          <div className="op-container op-container--left cw-hero-inner">
            <div className="cw-hero-layout">
              <div className="cw-hero-content">

                <h1 className="cw-hero-title" data-reveal style={delay(0)}>
                  Cash &amp; Working Capital
                  <span className="cw-hero-title-accent">Intelligence</span>
                </h1>

                <p className="cw-hero-subtitle" data-reveal style={delay(1, 130)}>Free up cash. Improve liquidity. Enable growth.</p>

                <p className="cw-hero-text" data-reveal style={delay(2, 130)}>
                  Use your data, business rules and AI to optimise working capital across receivables,
                  payables, inventory and cash — with real-time insights and predictive intelligence.
                </p>

                <div className="cw-hero-actions" data-reveal style={delay(3, 130)}>
                  <Link href="/tellusyourproblem#tell-details" className="fp-buttons fp-btn-primary">
                    Book a Discovery Session <span>→</span>
                  </Link>
                  <Link href="#overview" className="fp-buttons fp-hero-outline-btn">
                    Watch the Overview <span>↗</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============================ BENEFITS ============================ */}
        <section className="op-section op-section--white cw-benefits-section" data-purpose="benefits" aria-labelledby="cw-benefits-title">
          <div className="op-container">
            <div className="cw-benefits-heading" data-reveal>
              <span className="cw-benefits-eyebrow">CASH &amp; WORKING CAPITAL INTELLIGENCE</span>
              <h2 id="cw-benefits-title">Transform Working Capital into a Strategic Advantage</h2>
              <p>Unlock measurable business impact through smarter, data-driven cash and working capital management.</p>
            </div>
            <div className="cw-benefits-grid">
              {benefits.map((b, i) => (
                <article className={`cw-benefit-card cw-benefit-card--${i + 1}`} key={b.title} data-reveal style={delay(i, 90)}>
                  <span className="cw-benefit-number">{String(i + 1).padStart(2, '0')}</span>
                  <div className="cw-benefit-copy">
                    <h3>{b.title}</h3>
                    <p>{b.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ===================== KEY BUSINESS QUESTIONS ===================== */}
        <section className="op-section op-section--slate" data-purpose="business-questions" id="overview">
          <div className="op-container">
            <div className="sec-head" data-reveal>
              <div className="sec-badge">Business Value</div>
              <h2 className="sec-title">Key Business Questions</h2>
              <p className="sec-sub">Turn your working capital questions into intelligent decisions.</p>
            </div>

            <div className="q-grid">
              {questions.map((q, i) => (
                <div className="q-card" key={q.text} data-reveal style={delay(i, 90)}>
                  <span className={`q-icon tone-${q.tone}`}>
                    <Icon name={q.icon} />
                  </span>
                  <p className="q-text">{q.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ======================== INDUSTRY SCENARIOS ======================== */}
        <section className="op-section op-section--white" data-purpose="industry-scenarios">
          <div className="op-container">
            <div className="sec-head" data-reveal>
              <div className="sec-badge">Industry Applications</div>
              <h2 className="sec-title">Industry Scenarios</h2>
              <p className="sec-sub">Proven across industries with real-world templates and accelerators.</p>
            </div>

            <div className="ind-grid">
              {industries.map((item, i) => (
                <Link className="ind-card" href={item.href} key={item.title} data-reveal style={delay(i % 3, 110)}>
                  <div className="ind-image">
                    <img src={item.image} alt={`${item.title} industry scenario`} loading="lazy" />
                  </div>
                  <div className="ind-body">
                    <h3 className="ind-title">{item.title}</h3>
                    <p className="ind-text">{item.desc}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ======================== AI-ENABLED USE CASES ======================== */}
        <section className="op-section op-section--slate" data-purpose="use-cases" id="use-cases">
          <div className="op-container op-container--left">
            <div className="sec-head coverage-section-head" data-reveal>
              <div className="sec-badge">Applied AI</div>
              <h2 className="sec-title">AI-Enabled Use Cases</h2>
              <p className="sec-sub">From insight to action with applied AI.</p>
            </div>

            <div className="coverage-layout">
              <div className="coverage-main">
                <div className="coverage-timeline">
                  {useCases.map((item, i) => (
                    <article className="coverage-timeline-item" key={item.title} data-reveal style={delay(i, 80, 400)}>
                      <div className="coverage-timeline-marker" aria-hidden="true">
                        <span className="coverage-timeline-number">{String(i + 1).padStart(2, '0')}</span>
                        <span className="coverage-timeline-dot"></span>
                      </div>
                      <div className="coverage-timeline-content">
                        <h3 className="coverage-title">{item.title}</h3>
                        <p className="coverage-desc">{item.desc}</p>
                      </div>
                      <span className="coverage-timeline-link">Use Case <span aria-hidden="true">→</span></span>
                    </article>
                  ))}
                </div>
              </div>
              <div className="coverage-visual" data-reveal style={delay(1, 150)}>
                <img src="/assets/images/AI-Enabled-Use-Cases%201.jpg" alt="Cash and working capital analytics dashboard" loading="lazy" />
              </div>
            </div>
          </div>
        </section>

        {/* ================= 52 PROBLEMS + RESOURCES ================= */}
        <section className="op-section op-section--white cw-pr-section" data-purpose="problems-and-resources" id="resources">
          <div className="op-container">
            <div className="cw-pr-layout">
              <div className="cw-pr-problems">
                <div className="cw-pr-section-title" data-reveal>
                  <h2>52 Working Capital Problems We Solve</h2>
                  <p>Real problems. Practical solutions.</p>
                </div>
                <div className="cw-pr-card cw-pr-problem-card" data-reveal style={delay(1, 120)}>
                  <ol className="cw-pr-problem-list">
                    {problems.map((p, i) => (
                      <li key={p}>
                        <b>{String(i + 1).padStart(2, '0')}</b>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>

              <div className="cw-pr-resources">
                <div className="cw-pr-section-title" data-reveal style={delay(1, 100)}>
                  <h2>Resources &amp; Downloads</h2>
                  <p>Templates, frameworks and case examples.</p>
                </div>
                <div className="cw-pr-card cw-pr-resource-card" data-reveal style={delay(2, 120)}>
                  <div className="cw-pr-resource-list">
                    {resources.map((r) => (
                      <a className="cw-pr-resource" href="#contact" key={r.title} aria-label={`Download ${r.title}`}>
                        <span className={`cw-pr-file-icon cw-pr-file-${r.tone}`}>
                          <Icon name="file" />
                        </span>
                        <span className="cw-pr-resource-copy">
                          <strong>{r.title}</strong>
                          <small>{r.type}</small>
                        </span>
                        <span className="cw-pr-download" aria-hidden="true">
                          <Icon name="download" />
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="op-container cw-pr-cta-shell">
            <div className="cw-pr-cta-row" data-reveal>
              <Link href="/52problem#all-problems" className="cw-pr-view-link">
                View All 52 Problems <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        </section>

        {/* ======================= ENGAGEMENT JOURNEY ======================= */}
        <section className="op-section op-section--slate" data-purpose="engagement-journey">
          <div className="op-container">
            <div className="sec-head sec-head--center" data-reveal>
              <div className="sec-badge">The Journey Roadmap</div>
              <h2 className="sec-title">Our Engagement Journey</h2>
              <p className="sec-sub sec-sub--base">From understanding to measurable outcomes.</p>
            </div>

            <div className="cw-journey-scroll">
              <div className="cw-journey-track">
                {journey.map((step, i) => (
                  <div className="cw-journey-step" key={step.title} data-reveal style={delay(i, 110, 440)}>
                    <span className="cw-journey-num">{i + 1}</span>
                    <div className="cw-journey-content">
                      <h3 className="cw-journey-title">{step.title}</h3>
                      <p className="cw-journey-desc">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ========================= FINAL CTA ================================ */}
        <section className="cw-cta" data-purpose="final-call-to-action" id="contact">
          <img className="cw-cta-bg" src="/assets/images/cash-capital-cta.jpg" alt="" aria-hidden="true" />
          <div className="cw-cta-copy">
            <span className="cw-eyebrow" data-reveal>MORE KNOWLEDGE. MORE CAPABILITY. MORE VALUE. TOGETHER.</span>
            <h2 data-reveal style={delay(1, 120)}>Do More. Together.</h2>
            <p data-reveal style={delay(2, 120)}>
              Stronger cash. Healthier business. Greater opportunity. Let’s build a more resilient
              and profitable future.
            </p>
            <div className="cw-cta-actions" data-reveal style={delay(3, 120)}>
              <Link href="/tellusyourproblem#tell-details" className="fp-buttons fp-btn-primary">
                Talk to Our Team <span>→</span>
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}